import type { BadgeSemanticColor } from '@customerio/pluma-components/react';
import type { AppCredentialPayload, AppCredentialValue, PushPlatform } from '@/features/push-credentials';
import type { App, AppPlatform } from '@/services/db/app/entity';

export const APP_PLATFORMS: readonly PushPlatform[] = ['ios', 'android'];

/** The longest name the backend stores. */
export const MAX_APP_NAME_LENGTH = 191;

export type SharedFcmFile = {
  owner: PushPlatform;
  fileName: string;
  rawJson: string;
};

export type PlatformReuse = {
  /** A sibling credential already saved, reused by id. */
  reuseProjectId: string | null;
  reuseCredentialId: number | null;
  /** A sibling upload staged this session, reused by copying its file. */
  sharedFcm: SharedFcmFile | null;
};

export const NO_REUSE: PlatformReuse = { reuseProjectId: null, reuseCredentialId: null, sharedFcm: null };

export type PlatformDrafts = {
  iosEnabled: boolean;
  iosDraft: AppCredentialValue | null;
  androidEnabled: boolean;
  androidDraft: AppCredentialValue | null;
  /**
   * The platform whose staged upload the other one copied, while that copy
   * stands. A copy is identical to an upload of the same file, so the borrower
   * is recorded when the copy happens rather than inferred from the payloads.
   */
  copyOwner: PushPlatform | null;
};

export type SavedPlatform = {
  present: boolean;
  configured: boolean;
  identifier: string;
  credentialId: number | null;
};

const ABSENT_PLATFORM: SavedPlatform = { present: false, configured: false, identifier: '', credentialId: null };

function findPlatform(app: App | null, platform: PushPlatform): AppPlatform | undefined {
  return app?.platforms.find((entry) => entry.platform === platform);
}

/**
 * A platform can be registered without a key, so what makes it able to send is
 * its `credential_id`, not its presence.
 */
export function savedPlatformOf(app: App | null, platform: PushPlatform): SavedPlatform {
  const entry = findPlatform(app, platform);
  if (entry == null) {
    return ABSENT_PLATFORM;
  }
  return {
    present: true,
    configured: entry.credential_id != null,
    identifier: entry.identifier,
    credentialId: entry.credential_id ?? null,
  };
}

export function draftOf(drafts: PlatformDrafts, platform: PushPlatform): AppCredentialValue | null {
  return platform === 'ios' ? drafts.iosDraft : drafts.androidDraft;
}

export function isEnabled(drafts: PlatformDrafts, platform: PushPlatform): boolean {
  return platform === 'ios' ? drafts.iosEnabled : drafts.androidEnabled;
}

/**
 * Ends any recorded borrowing. This is the one place a draft changes, which is
 * what keeps `copyOwner` from outliving the copy it names.
 */
function withDraft(drafts: PlatformDrafts, platform: PushPlatform, draft: AppCredentialValue | null): PlatformDrafts {
  const released: PlatformDrafts = { ...drafts, copyOwner: null };
  return platform === 'ios' ? { ...released, iosDraft: draft } : { ...released, androidDraft: draft };
}

function withEnabled(drafts: PlatformDrafts, platform: PushPlatform, enabled: boolean): PlatformDrafts {
  return platform === 'ios' ? { ...drafts, iosEnabled: enabled } : { ...drafts, androidEnabled: enabled };
}

function withCopyOwner(drafts: PlatformDrafts, copyOwner: PushPlatform | null): PlatformDrafts {
  return { ...drafts, copyOwner };
}

export function siblingOf(platform: PushPlatform): PushPlatform {
  return platform === 'ios' ? 'android' : 'ios';
}

export function asFcmUpload(value: AppCredentialValue | null): AppCredentialPayload | null {
  if (value == null || 'credential_id' in value) {
    return null;
  }
  return value.action_type === 'push_fcm' ? value : null;
}

export function isReuseLink(value: AppCredentialValue | null): boolean {
  return value != null && 'credential_id' in value;
}

export function initialDrafts(app: App | null): PlatformDrafts {
  return {
    iosEnabled: savedPlatformOf(app, 'ios').present,
    iosDraft: null,
    androidEnabled: savedPlatformOf(app, 'android').present,
    androidDraft: null,
    copyOwner: null,
  };
}

export function rowIdentifier(app: App | null, drafts: PlatformDrafts, platform: PushPlatform): string {
  const draft = draftOf(drafts, platform);
  if (draft != null) {
    return draft.identifier;
  }
  return isEnabled(drafts, platform) ? savedPlatformOf(app, platform).identifier : '';
}

export function isConfigured(app: App | null, drafts: PlatformDrafts, platform: PushPlatform): boolean {
  if (!isEnabled(drafts, platform)) {
    return false;
  }
  return savedPlatformOf(app, platform).configured || draftOf(drafts, platform) != null;
}

export function isPending(app: App | null, drafts: PlatformDrafts, platform: PushPlatform): boolean {
  return draftOf(drafts, platform) != null || isEnabled(drafts, platform) !== savedPlatformOf(app, platform).present;
}

export function statusLabel(configured: boolean): string {
  return configured ? 'Configured' : 'Not configured';
}

export type PlatformStatusColor = Extract<BadgeSemanticColor, 'success' | 'neutral'>;

export function statusColor(configured: boolean): PlatformStatusColor {
  return configured ? 'success' : 'neutral';
}

function sharedFileFrom(owner: PushPlatform, payload: AppCredentialPayload): SharedFcmFile {
  return { owner, fileName: payload.credential.username, rawJson: payload.credential.password };
}

function stagedFcmShare(drafts: PlatformDrafts, owner: PushPlatform): PlatformReuse | null {
  if (!isEnabled(drafts, owner)) {
    return null;
  }
  const upload = asFcmUpload(draftOf(drafts, owner));
  if (upload == null) {
    return null;
  }
  return { reuseProjectId: null, reuseCredentialId: null, sharedFcm: sharedFileFrom(owner, upload) };
}

function androidSavedReuse(app: App | null, drafts: PlatformDrafts): PlatformReuse {
  const android = savedPlatformOf(app, 'android');
  if (!drafts.androidEnabled || !android.configured || drafts.androidDraft !== null) {
    return NO_REUSE;
  }
  return { reuseProjectId: android.identifier, reuseCredentialId: android.credentialId, sharedFcm: null };
}

/**
 * Android always sends through FCM, so iOS may reuse its saved credential. A
 * saved iOS credential's provider is not known from the app alone, so Android
 * only reuses an iOS credential staged as FCM in this session.
 */
export function reuseFor(app: App | null, drafts: PlatformDrafts, platform: PushPlatform): PlatformReuse {
  const staged = stagedFcmShare(drafts, siblingOf(platform));
  if (staged !== null) {
    return staged;
  }
  return platform === 'ios' ? androidSavedReuse(app, drafts) : NO_REUSE;
}

export function initialIdentifierFor(app: App | null, drafts: PlatformDrafts, platform: PushPlatform): string {
  const current = rowIdentifier(app, drafts, platform);
  if (platform === 'ios' || current !== '') {
    return current;
  }
  const iosUpload = asFcmUpload(drafts.iosDraft);
  return iosUpload?.identifier ?? '';
}

function isCopyOf(candidate: AppCredentialValue | null, source: AppCredentialValue | null): boolean {
  const candidateUpload = asFcmUpload(candidate);
  const sourceUpload = asFcmUpload(source);
  if (candidateUpload == null || sourceUpload == null) {
    return false;
  }
  return candidateUpload.credential.password === sourceUpload.credential.password;
}

/** Expects `drafts` with any copy taken from `platform` already dropped. */
function copyOwnerAfter(
  drafts: PlatformDrafts,
  platform: PushPlatform,
  staged: AppCredentialValue | null,
): PushPlatform | null {
  const sibling = siblingOf(platform);
  return isCopyOf(staged, draftOf(drafts, sibling)) ? sibling : null;
}

/**
 * Reusing a sibling's in-session upload copies the file into this platform's own
 * payload rather than linking to it, so replacing the source would leave the copy
 * saved against a service account nobody chose, while the row still reads as
 * configured. Drop the copy so the platform is reconfigured deliberately.
 *
 * Only the borrower's draft goes, never the one it borrowed from, which is why
 * the pairing is read from `copyOwner` instead of from the identical payloads.
 */
function dropBorrowedCopyOf(drafts: PlatformDrafts, owner: PushPlatform): PlatformDrafts {
  return drafts.copyOwner === owner ? withDraft(drafts, siblingOf(owner), null) : drafts;
}

/** An iOS credential that reuses Android's is only valid while that one is unchanged. */
function dropReusedIosLink(drafts: PlatformDrafts): PlatformDrafts {
  return isReuseLink(drafts.iosDraft) ? withDraft(drafts, 'ios', null) : drafts;
}

export function stageCredential(
  drafts: PlatformDrafts,
  platform: PushPlatform,
  value: AppCredentialValue,
): PlatformDrafts {
  // The copy has to go before the new owner is read, or re-staging the very
  // file the sibling copied reads as this platform borrowing from that copy.
  const released = dropBorrowedCopyOf(drafts, platform);
  const copyOwner = copyOwnerAfter(released, platform, value);
  let next = withDraft(withEnabled(released, platform, true), platform, value);
  if (platform === 'android') {
    next = dropReusedIosLink(next);
  }
  return withCopyOwner(next, copyOwner);
}

/** Null when there is no saved credential to restore, so the caller opens the setup form. */
export function restoreSavedPlatform(
  app: App | null,
  drafts: PlatformDrafts,
  platform: PushPlatform,
): PlatformDrafts | null {
  if (!savedPlatformOf(app, platform).configured) {
    return null;
  }
  return withDraft(withEnabled(drafts, platform, true), platform, null);
}

/** For a platform already written, so a retry does not send its credential again. */
export function clearDraft(drafts: PlatformDrafts, platform: PushPlatform): PlatformDrafts {
  return withDraft(drafts, platform, null);
}

export function disablePlatform(drafts: PlatformDrafts, platform: PushPlatform): PlatformDrafts {
  const released = dropBorrowedCopyOf(drafts, platform);
  let next = withDraft(withEnabled(released, platform, false), platform, null);
  if (platform === 'android') {
    next = dropReusedIosLink(next);
  }
  return next;
}

export function effectivePayload(drafts: PlatformDrafts, platform: PushPlatform): AppCredentialValue | null {
  return isEnabled(drafts, platform) ? draftOf(drafts, platform) : null;
}

export function isDisabledOnSave(app: App | null, drafts: PlatformDrafts, platform: PushPlatform): boolean {
  return savedPlatformOf(app, platform).present && !isEnabled(drafts, platform);
}

export function arePlatformsComplete(app: App | null, drafts: PlatformDrafts): boolean {
  return APP_PLATFORMS.every(
    (platform) =>
      !isEnabled(drafts, platform) || savedPlatformOf(app, platform).configured || draftOf(drafts, platform) != null,
  );
}

export function arePlatformsDirty(app: App | null, drafts: PlatformDrafts): boolean {
  return APP_PLATFORMS.some(
    (platform) =>
      draftOf(drafts, platform) != null || isEnabled(drafts, platform) !== savedPlatformOf(app, platform).present,
  );
}
